/**
 * Smart Wishlist customisations:
 * 1) Price cleanup on /a/wishlist ("Rp15.700,00 IDR" → "Rp 15.700")
 * 2) Variant title and photo corrections on the wishlist cards
 * 3) Scroll-reveal animation matching the collection grid
 * 4) Header cart badge sync after the app adds to cart
 * 5) PDP toast when a product is added to or removed from the wishlist
 */
(function () {
  /* ------------------------------------------------------------------ */
  /* Price formatting                                                    */
  /* ------------------------------------------------------------------ */
  function formatPriceText(text) {
    if (!text) return text;
    var t = String(text).replace(/\u00a0/g, ' ').trim();
    if (!t) return t;

    t = t.replace(/\s*[A-Z]{3}\s*$/i, '').trim();
    t = t.replace(/([,.])0{2}\s*$/, '');
    t = t.replace(/^(Rp)\s*/i, 'Rp ');

    return t.trim();
  }

  function formatPriceNode(node) {
    if (!node) return;

    var strike = node.querySelector('s');
    if (strike) {
      strike.textContent = formatPriceText(strike.textContent);
      return;
    }

    if (node.childNodes.length === 1 && node.firstChild.nodeType === 3) {
      node.textContent = formatPriceText(node.textContent);
      return;
    }

    node.childNodes.forEach(function (child) {
      if (child.nodeType === 3) {
        var next = formatPriceText(child.textContent);
        if (next !== child.textContent) child.textContent = next;
      }
    });
  }

  function formatWishlistPrices(root) {
    var scope = root || document.querySelector('#all.wishlistpage') || document;
    scope.querySelectorAll('.product .price, .product .compare-price').forEach(formatPriceNode);
  }

  /* ------------------------------------------------------------------ */
  /* Variant title visibility                                            */
  /*                                                                     */
  /* Smart Wishlist exposes `is_true_variant` as data-is_chosen_variant  */
  /* on each card's Add to Cart button. Product entries are 0; variants  */
  /* explicitly selected on PDP are 1. This survives login/device sync.  */
  /* ------------------------------------------------------------------ */
  function isExplicitVariantCard(card) {
    var button = card.querySelector('.addsingleproduct');
    return button && Number(button.getAttribute('data-is_chosen_variant')) === 1;
  }

  function applyVariantTitleVisibility(root) {
    var scope = root || document.querySelector('#all.wishlistpage');
    if (!scope) return;

    scope.querySelectorAll('.product').forEach(function (card) {
      var title = card.querySelector('.variant_title');
      if (!title) return;
      title.classList.toggle('sw-variant-hidden', !isExplicitVariantCard(card));
    });
  }

  /* ------------------------------------------------------------------ */
  /* Variant image                                                       */
  /*                                                                     */
  /* The app's API returns the product's main image even when a specific */
  /* variant was saved, so a chosen variant shows the wrong photo. Look  */
  /* the variant image up through Shopify's product JSON and swap it in. */
  /* ------------------------------------------------------------------ */
  var productRequests = {};

  /* Resolved variant images are remembered for the session so a reload can
     swap them in before the browser paints, with no lookup round trip. */
  var IMAGE_CACHE_KEY = 'sw_variant_images';

  function readImageCache() {
    try {
      return JSON.parse(window.sessionStorage.getItem(IMAGE_CACHE_KEY)) || {};
    } catch (err) {
      return {};
    }
  }

  function cacheVariantImage(variantId, src) {
    try {
      var cache = readImageCache();
      if (cache[variantId] === src) return;
      cache[variantId] = src;
      window.sessionStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(cache));
    } catch (err) {
      /* Private mode or a full quota — the lookup still works, just slower. */
    }
  }

  function fetchProduct(handle) {
    if (productRequests[handle]) return productRequests[handle];

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    productRequests[handle] = fetch(root + 'products/' + handle + '.js', {
      credentials: 'same-origin'
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });

    return productRequests[handle];
  }

 function handleFromCard(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var match = link.getAttribute('href').match(/\/products\/([^?#/]+)/);
    return match ? match[1] : null;
  }

  function revealImage(img) {
    img.classList.remove('sw-image-pending');
  }

  function showVariantImage(img, src) {
    /* Prefer the master CDN file. Older session-cache entries may still carry
       a forced `width=800` from a previous build. */
    var next = src;
    try {
      var url = new URL(src, window.location.origin);
      url.searchParams.delete('width');
      next = url.toString();
    } catch (err) {}

    if (img.getAttribute('src') === next) {
      revealImage(img);
      return;
    }

    img.addEventListener('load', function () { revealImage(img); }, { once: true });
    img.addEventListener('error', function () { revealImage(img); }, { once: true });
    img.setAttribute('src', next);

    /* Neither event fires for an image already decoded in cache. */
    window.setTimeout(function () { revealImage(img); }, 2000);
  }

  function applyVariantImages(root) {
    var scope = root || document.querySelector('#all.wishlistpage');
    if (!scope || typeof fetch !== 'function') return;

    var cache = readImageCache();

    scope.querySelectorAll('.product').forEach(function (card) {
      var img = card.querySelector('.image img');
      if (!img || img.dataset.swVariantImage === '1') return;

      var variantId = String(card.dataset.variantid);
      /* Only when the shopper picked the variant — otherwise the saved variant
         is just the product's first one and its photo would mislead. */
      if (!isExplicitVariantCard(card)) return;

      var handle = handleFromCard(card);
      if (!handle) return;

      img.dataset.swVariantImage = '1';

      if (cache[variantId]) {
        showVariantImage(img, cache[variantId]);
        return;
      }

      /* Hide instead of letting the app's product photo show and then jump to
         the variant once the lookup resolves. */
      img.classList.add('sw-image-pending');

      fetchProduct(handle).then(function (product) {
        if (!product || !Array.isArray(product.variants)) {
          revealImage(img);
          return;
        }

        var variant = product.variants.filter(function (v) {
          return String(v.id) === variantId;
        })[0];

        var src = variant && variant.featured_image && variant.featured_image.src;
        if (!src) {
          revealImage(img);
          return;
        }

        cacheVariantImage(variantId, src);
        showVariantImage(img, src);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Scroll reveal                                                       */
  /*                                                                     */
  /* Reuses the theme's own utility so wishlist cards fade in exactly    */
  /* like the collection grid, staggered four across per row.            */
  /* ------------------------------------------------------------------ */
  function applyRevealAnimation(root) {
    var scope = root || document.querySelector('#all.wishlistpage');
    if (!scope || typeof window.initScrollReveal !== 'function') return;

    scope.querySelectorAll('[id^="product_"]').forEach(function (card, index) {
      if (card.classList.contains('reveal-on-scroll')) return;
      card.setAttribute('data-reveal-delay', String((index % 4) * 100));
      card.classList.add('reveal-on-scroll');
    });

    window.initScrollReveal(scope);
  }

  function ensureWishlistBreadcrumb() {
    var page = document.querySelector('#all.wishlistpage');
    if (!page || page.querySelector('.wishlist-breadcrumb-bar')) return;

    var titleEl = page.querySelector('h1.titletext');
    var label = titleEl ? titleEl.textContent.trim() : 'Wishlist';

    var bar = document.createElement('div');
    bar.className = 'page-breadcrumb wishlist-breadcrumb-bar';
    bar.innerHTML =
      '<div class="page-breadcrumb__inner">' +
        '<nav class="breadcrumb wishlist-breadcrumb" aria-label="Breadcrumb">' +
          '<span class="breadcrumb__item"><a href="/">Home</a></span>' +
          '<span class="breadcrumb__sep" aria-hidden="true">/</span>' +
          '<span class="breadcrumb__item breadcrumb__item--active" aria-current="page">' +
            label +
          '</span>' +
        '</nav>' +
      '</div>';

    page.insertBefore(bar, page.firstChild);
  }

  function refreshWishlistPage(root) {
    ensureWishlistBreadcrumb();
    formatWishlistPrices(root);
    applyVariantTitleVisibility(root);
    applyVariantImages(root);
    applyRevealAnimation(root);
  }

  window.WishlistLoadedCallback = function () {
    refreshWishlistPage();
    disableAppReloadAfterAddToCart();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      refreshWishlistPage();
    });
  } else {
    refreshWishlistPage();
  }

  var bookmarks = document.getElementById('bookmarks');
  if (bookmarks && typeof MutationObserver !== 'undefined') {
    var refreshTimer;
    new MutationObserver(function () {
      /* Both run before the browser paints the freshly inserted cards, so the
         wrong photo never shows and the fade starts from zero. Text can wait
         for the debounce. */
      applyVariantImages(bookmarks);
      applyRevealAnimation(bookmarks);

      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(function () {
        refreshWishlistPage(bookmarks);
      }, 50);
    }).observe(bookmarks, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ */
  /* Header cart badge                                                   */
  /*                                                                     */
  /* Adding to cart from the wishlist reaches Shopify straight away, but */
  /* the app only refreshes the counter element configured in its own    */
  /* settings, so this theme's badge stayed stale until a reload. Mirror */
  /* what the theme's own add-to-cart does instead.                      */
  /* ------------------------------------------------------------------ */
  function refreshHeaderCartBadge() {
    if (window.BohopanaCart && typeof window.BohopanaCart.refreshFromCart === 'function') {
      return window.BohopanaCart.refreshFromCart({ showNotice: true });
    }

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll('.header-action-btn__badge').forEach(function (badge) {
          badge.textContent = cart.item_count > 0 ? '+' + cart.item_count : '';
          badge.style.display = cart.item_count > 0 ? '' : 'none';
        });
      })
      .catch(function () {});
  }

  /* The app reloads the whole page a second after adding to cart, which is
     only there to resync a counter we now update ourselves. */
  function disableAppReloadAfterAddToCart() {
    window.reload_after_addcart = 0;
  }

  /* The app appends a spinner to the add button, then tries to restore the
     button with `$(this).html(button_html)` inside its AJAX callback, where
     `this` is the jQuery settings object rather than the button. That restore
     never lands, so the spinner is removed here instead. */
  function clearAddButtonSpinners() {
    document.querySelectorAll('#all.wishlistpage a.addbutton .fa-refresh').forEach(function (spinner) {
      spinner.parentNode.removeChild(spinner);
    });
  }

  /* The app drives every cart call through jQuery, so one global hook
     covers both the per-card button and Add All. */
  function watchAppCartRequests() {
    if (!window.jQuery) return false;

    disableAppReloadAfterAddToCart();

    window.jQuery(document).on('ajaxComplete', function (event, xhr, settings) {
      var url = settings && settings.url ? String(settings.url) : '';
      if (url.indexOf('/cart/add') === -1 && url.indexOf('/cart/update') === -1) return;
      disableAppReloadAfterAddToCart();
      refreshHeaderCartBadge();
      clearAddButtonSpinners();
      /* The app writes the button markup back after its own handler returns. */
      window.setTimeout(clearAddButtonSpinners, 600);
    });

    return true;
  }

  /* ------------------------------------------------------------------ */
  /* Remove confirmation modal                                           */
  /*                                                                     */
  /* The app fills #remove_modal with the product master photo + title,  */
  /* even when the card is a chosen variant (e.g. MERAH). Copy the card  */
  /* image / title / variant label into the modal when it opens.         */
  /* ------------------------------------------------------------------ */
  var pendingRemoveCard = null;
  var removeModalSyncing = false;
  var removeModalSyncedForOpen = false;

  function cardProductTitle(card) {
    var el = card.querySelector('.product_title a, .product_title, h3 a, h3');
    return el ? el.textContent.trim() : '';
  }

  function cardVariantLabel(card) {
    if (!isExplicitVariantCard(card)) return '';
    var el = card.querySelector('.variant_title');
    if (!el || el.classList.contains('sw-variant-hidden')) return '';
    return el.textContent.trim().toUpperCase();
  }

  function cardImageSrc(card) {
    var img = card.querySelector('.image img');
    if (!img) return '';
    return img.currentSrc || img.getAttribute('src') || '';
  }

  function relocateModalActions(modal) {
    if (!modal) return;
    var row = modal.querySelector('.modal-body > .row') || modal.querySelector('.modal-body .row');
    var footer = modal.querySelector('.modal-content > .modal-footer')
      || modal.querySelector('.modal-body > .modal-footer')
      || modal.querySelector('.modal-footer');
    if (!row || !footer) return;
    if (footer.parentElement === row) return;
    footer.classList.add('sw-modal-actions');
    row.appendChild(footer);
  }

  function syncRemoveModal(card) {
    if (!card || removeModalSyncing) return;
    removeModalSyncing = true;

    try {
      var modal = document.getElementById('remove_modal');
      if (!modal) return;

      relocateModalActions(modal);

      var imgWrap = document.getElementById('remove_modal_image') || modal.querySelector('#remove_modal_image');
      var titleEl = document.getElementById('remove_modal_title') || modal.querySelector('#remove_modal_title');
      var modalImg = imgWrap ? imgWrap.querySelector('img') : null;

      var src = cardImageSrc(card);
      if (modalImg && src && modalImg.getAttribute('src') !== src) {
        modalImg.removeAttribute('srcset');
        modalImg.setAttribute('src', src);
      }

      if (titleEl) {
        var name = cardProductTitle(card);
        var variant = cardVariantLabel(card);
        if (name) {
          var nextHtml = variant
            ? '<span class="sw-remove-modal__name">' + escapeHtml(name) + '</span>' +
              '<span class="sw-remove-modal__variant">' + escapeHtml(variant) + '</span>'
            : '<span class="sw-remove-modal__name">' + escapeHtml(name) + '</span>';
          if (titleEl.innerHTML !== nextHtml) titleEl.innerHTML = nextHtml;
        }
      }
    } finally {
      removeModalSyncing = false;
    }
  }

  function rememberRemoveCard(e) {
    var trigger = e.target.closest && e.target.closest(
      'a.removebutton, button.removebutton, .removebutton, [data-target="#remove_modal"], [href="#remove_modal"]'
    );
    if (!trigger) return;
    var card = trigger.closest('.product, [id^="product_"]');
    if (!card) return;
    pendingRemoveCard = card;
    removeModalSyncedForOpen = false;
    /* Defer until Bootstrap has opened the modal. */
    window.setTimeout(function () {
      relocateModalActions(document.getElementById('remove_modal'));
      relocateModalActions(document.getElementById('remove_all_modal'));
      if (!pendingRemoveCard) return;
      syncRemoveModal(pendingRemoveCard);
      removeModalSyncedForOpen = true;
    }, 50);
    window.setTimeout(function () {
      relocateModalActions(document.getElementById('remove_modal'));
      relocateModalActions(document.getElementById('remove_all_modal'));
      if (pendingRemoveCard) syncRemoveModal(pendingRemoveCard);
    }, 200);
  }

  function onRemoveModalClassChange() {
    var modal = document.getElementById('remove_modal');
    if (!modal) return;

    var isOpen = modal.classList.contains('in') || modal.classList.contains('show');
    if (!isOpen) {
      removeModalSyncedForOpen = false;
      return;
    }

    relocateModalActions(modal);

    if (removeModalSyncedForOpen || !pendingRemoveCard) return;
    syncRemoveModal(pendingRemoveCard);
    removeModalSyncedForOpen = true;
  }

  document.addEventListener('click', rememberRemoveCard, true);

  function bindRemoveModalObserver() {
    var modal = document.getElementById('remove_modal');
    var allModal = document.getElementById('remove_all_modal');
    if (typeof MutationObserver === 'undefined') return !!(modal || allModal);

    if (modal && !modal.dataset.swObserverBound) {
      modal.dataset.swObserverBound = '1';
      new MutationObserver(onRemoveModalClassChange).observe(modal, {
        attributes: true,
        attributeFilter: ['class']
      });
      relocateModalActions(modal);
    }

    if (allModal && !allModal.dataset.swObserverBound) {
      allModal.dataset.swObserverBound = '1';
      new MutationObserver(function () {
        if (allModal.classList.contains('in') || allModal.classList.contains('show')) {
          relocateModalActions(allModal);
        }
      }).observe(allModal, {
        attributes: true,
        attributeFilter: ['class']
      });
      relocateModalActions(allModal);
    }

    return !!(modal || allModal);
  }

  if (!bindRemoveModalObserver()) {
    var removeModalWait = window.setInterval(function () {
      if (bindRemoveModalObserver()) window.clearInterval(removeModalWait);
    }, 300);
    window.setTimeout(function () { window.clearInterval(removeModalWait); }, 15000);
  }

  /* ------------------------------------------------------------------ */
  /* App dialog after add-to-cart                                        */
  /*                                                                     */
  /* `#message_modal` repeats what the theme's cart notice already says. */
  /* CSS hides the dialog itself; Bootstrap still leaves its backdrop and */
  /* scroll lock behind, which is what gets cleaned up here.              */
  /* ------------------------------------------------------------------ */
  function dismissAppMessageModal() {
    var modal = document.getElementById('message_modal');
    if (!modal) return;

    var isOpen = modal.classList.contains('in') || modal.classList.contains('show');
    if (!isOpen) return;

    /* Never tear down a live remove/share dialog's backdrop. */
    var removeModal = document.getElementById('remove_modal');
    if (removeModal && (removeModal.classList.contains('in') || removeModal.classList.contains('show'))) {
      modal.classList.remove('in', 'show');
      modal.setAttribute('aria-hidden', 'true');
      return;
    }

    modal.classList.remove('in', 'show');
    modal.setAttribute('aria-hidden', 'true');

    document.querySelectorAll('.modal-backdrop').forEach(function (backdrop) {
      backdrop.parentNode.removeChild(backdrop);
    });

    document.body.classList.remove('modal-open');
    document.body.style.paddingRight = '';
  }

  if (bookmarks) {
    new MutationObserver(dismissAppMessageModal).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true
    });
  }

  if (bookmarks && !watchAppCartRequests()) {
    /* jQuery ships with the app bundle, which loads after this script. */
    var jqueryWait = window.setInterval(function () {
      if (watchAppCartRequests()) window.clearInterval(jqueryWait);
    }, 200);
    window.setTimeout(function () {
      window.clearInterval(jqueryWait);
    }, 15000);
  }

  /* ------------------------------------------------------------------ */
  /* Add-to-wishlist toast (PDP)                                         */
  /* ------------------------------------------------------------------ */
  var toastEl = null;
  var toastHideTimer = null;

  function getToast() {
    if (toastEl) return toastEl;

    toastEl = document.createElement('div');
    toastEl.className = 'sw-wishlist-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.innerHTML =
      '<span class="sw-wishlist-toast__icon" aria-hidden="true"></span>' +
      '<span class="sw-wishlist-toast__text"></span>';
    document.body.appendChild(toastEl);
    return toastEl;
  }

  function productTitle() {
    var el = document.querySelector('.pdp-info__name');
    return el ? el.textContent.trim() : 'Produk';
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function renderToast(html) {
    var toast = getToast();
    toast.querySelector('.sw-wishlist-toast__text').innerHTML = html;
    toast.classList.add('is-visible');
    clearTimeout(toastHideTimer);
    toastHideTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 3200);
  }

  function showWishlistToast(action) {
    var title = escapeHtml(productTitle());

    if (action === 'remove') {
      renderToast('<strong>' + title + '</strong> dihapus dari wishlist.');
      return;
    }

    renderToast(
      '<span class="sw-wishlist-toast__message"><strong>' + title + '</strong> sudah masuk wishlist.</span>' +
      '<a class="sw-wishlist-toast__link" href="/a/wishlist">Lihat wishlist</a>'
    );
  }

  /* Lets the PDP script reuse the same toast for non-wishlist messages. */
  window.SmartWishlistNotice = renderToast;

  function pdpWishlistButton() {
    return document.querySelector('[data-pdp-wishlist-toggle]');
  }

  function playPop(btn) {
    if (!btn) return;
    btn.classList.remove('sw-pop');
    void btn.offsetWidth;
    btn.classList.add('sw-pop');
  }

  function onWishlistAdded() {
    var btn = pdpWishlistButton();
    if (!btn) return;
    btn.classList.add('bookmarked');
    btn.classList.remove('unbookmarked');
    btn.classList.remove('sw-removed');
    btn.setAttribute('aria-label', 'Remove from Wishlist');
    playPop(btn);
    showWishlistToast('add');
  }

  function onWishlistRemoved() {
    var btn = pdpWishlistButton();
    if (!btn) return;
    btn.classList.remove('bookmarked');
    btn.classList.add('unbookmarked');
    btn.classList.add('sw-removed');
    btn.setAttribute('aria-label', 'Add to Wishlist');
    playPop(btn);
    showWishlistToast('remove');
  }

  /* Official Smart Wishlist callbacks — fire only on real add/remove clicks,
     not when we sync data-variant / bookmarked state after option changes. */
  window.SWCallbackExpressWishlistCollectionItemAdded = onWishlistAdded;
  window.SWCallbackExpressWishlistCollectionItemRemoved = onWishlistRemoved;
  window.SWCallbackCustomerWishlistCollectionItemAdded = onWishlistAdded;
  window.SWCallbackCustomerWishlistCollectionItemRemoved = onWishlistRemoved;
  window.SWCallbackExpressWishlistProductItemAdded = onWishlistAdded;
  window.SWCallbackExpressWishlistProductItemRemoved = onWishlistRemoved;
  window.SWCallbackCustomerWishlistProductItemAdded = onWishlistAdded;
  window.SWCallbackCustomerWishlistProductItemRemoved = onWishlistRemoved;

  function bindWishlistButton(btn) {
    if (!btn || btn.dataset.swToastBound === '1') return;
    btn.dataset.swToastBound = '1';
    btn.addEventListener('animationend', function () {
      btn.classList.remove('sw-pop');
    });
  }

  function initWishlistToasts() {
    document
      .querySelectorAll('[data-pdp-wishlist-toggle]')
      .forEach(bindWishlistButton);

    var wrap = document.querySelector('.pdp-info__wishlist-wrap');
    if (wrap && typeof MutationObserver !== 'undefined') {
      new MutationObserver(function () {
        wrap.querySelectorAll('[data-pdp-wishlist-toggle]').forEach(bindWishlistButton);
      }).observe(wrap, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlistToasts);
  } else {
    initWishlistToasts();
  }
})();
